import { Clapperboard, Image as ImageIcon, LayoutGrid, MapPin, Package, Sparkles, Users, Video } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "@/hooks/use-translation"
import { cn } from "@/lib/utils"
import { UI_ACCENT_FILLED } from "@/lib/ui-selection"
import { PostProductionIcon } from "@/components/icons/post-production-icon"
import { CollapsedNavPeekProvider, SidebarTooltip } from "@/components/ui/sidebar-tooltip"
import type { LucideIcon } from "lucide-react"

export type ShotModalKey =
  | "preview"
  | "characters"
  | "location"
  | "props"
  | "transcription"
  | "storyboard_versions"
  | "shooting_board_versions"
  | "video_versions"
  | "post_production_versions"

export type ShotLeftPanelKey =
  | "prompt"
  | "assistant"
  | "characters"
  | "location"
  | "props"
  | "storyboard_versions"
  | "shooting_board_versions"
  | "video_versions"
  | "post_production_versions"
  | "flow"

type Entry = { key: ShotModalKey; icon: LucideIcon; dividerAfter?: boolean }

const ENTRIES: Entry[] = [
  { key: "preview", icon: Clapperboard },
  { key: "characters", icon: Users },
  { key: "location", icon: MapPin },
  { key: "props", icon: Package, dividerAfter: true },
  { key: "storyboard_versions", icon: ImageIcon },
  { key: "shooting_board_versions", icon: LayoutGrid },
  { key: "video_versions", icon: Video },
  { key: "post_production_versions", icon: PostProductionIcon },
]

export type ShotModalCounts = Partial<Record<ShotModalKey, number>>

type Props = {
  current: ShotModalKey
  onSwitch: (key: ShotModalKey) => void
  counts?: ShotModalCounts
  className?: string
  /** Desktop shot panel only, when flow mode is unavailable. Other hosts must omit this. */
  showAssistant?: boolean
  assistantActive?: boolean
  onAssistantClick?: () => void
  /** Parent rail owns the peek provider (mode toggle + entities share one hover). */
  embedded?: boolean
}

export function useShotModalLabels() {
  const { t } = useTranslation("shot")
  return (key: ShotModalKey) => t(`sidebar.${key}`)
}

/** @deprecated Use useShotModalLabels() inside components */
export const SHOT_MODAL_LABELS: Record<ShotModalKey, string> = {
  preview: "Prompts",
  characters: "Characters",
  location: "Location",
  props: "Props",
  transcription: "Transcription",
  storyboard_versions: "Frame versions",
  shooting_board_versions: "Shooting board",
  video_versions: "Video versions",
  post_production_versions: "Processing",
}

const RAIL_BUTTON_CLASS =
  "relative flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg transition-colors"

export function ShotModalSidebar({
  current,
  onSwitch,
  counts,
  className,
  showAssistant = false,
  assistantActive = false,
  onAssistantClick,
  embedded = false,
}: Props) {
  const { t } = useTranslation("shot")
  const assistantLabel = t("sidebar.assistant")
  const [railHovered, setRailHovered] = useState(false)
  const showRailDivider = showAssistant
  const hidePreview = showAssistant || embedded

  const rail = (
    <div
      className={cn("flex flex-row items-center gap-1.5 md:flex-col md:pl-2", className)}
      onMouseEnter={embedded ? undefined : () => setRailHovered(true)}
      onMouseLeave={embedded ? undefined : () => setRailHovered(false)}
    >
      {showAssistant ? (
        <SidebarTooltip label={assistantLabel} elevated tone="accent">
          <button
            type="button"
            aria-label={assistantLabel}
            onClick={onAssistantClick}
            className={cn(
              RAIL_BUTTON_CLASS,
              assistantActive
                ? cn(UI_ACCENT_FILLED, "dark:shadow-lg dark:shadow-violet-600/30 light:shadow-none")
                : cn(
                    "text-ui-accent-text ring-1 ring-ui-accent/55 ring-inset",
                    "dark:bg-ui-accent/20 dark:hover:bg-ui-accent/30",
                    "light:bg-ui-accent-soft light:hover:bg-ui-accent/20",
                  ),
            )}
          >
            <Sparkles className="h-4 w-4" strokeWidth={2} />
          </button>
        </SidebarTooltip>
      ) : null}
      {showRailDivider ? (
        <div className="mx-0.5 h-5 w-px shrink-0 bg-ui-surface-raised md:mx-auto md:my-0.5 md:h-px md:w-5" />
      ) : null}
      {ENTRIES.filter((entry) => !(hidePreview && entry.key === "preview")).map(({ key, icon: Icon, dividerAfter }) => {
        const active = key === current
        const count = counts?.[key]
        const label = t(`sidebar.${key}`)
        return (
          <div key={key} className="contents">
            <SidebarTooltip label={label} elevated>
              <button
                type="button"
                aria-label={label}
                onClick={() => onSwitch(key)}
                className={cn(
                  RAIL_BUTTON_CLASS,
                  active
                    ? cn(UI_ACCENT_FILLED, "dark:shadow-lg dark:shadow-violet-600/30 light:shadow-none")
                    : "text-ui-text-secondary hover:text-ui-text-primary dark:bg-zinc-800 hover:dark:bg-zinc-700 light:bg-clay-2/90 light:hover:bg-clay-3/90",
                )}
              >
                <Icon className="h-4 w-4" strokeWidth={2} />
                {count != null && count > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold text-ui-text-primary tabular-nums dark:bg-zinc-700 light:bg-clay-3">
                    {count}
                  </span>
                )}
              </button>
            </SidebarTooltip>
            {dividerAfter && (
              <div className="mx-0.5 h-5 w-px shrink-0 bg-ui-surface-raised md:mx-auto md:my-0.5 md:h-px md:w-5" />
            )}
          </div>
        )
      })}
    </div>
  )

  if (embedded) return rail

  return (
    <CollapsedNavPeekProvider enabled railHovered={railHovered}>
      {rail}
    </CollapsedNavPeekProvider>
  )
}
